// src/lib/pto/quote.ts
import { formatDayCount, round2 } from "@/lib/pto/days";
import type { IsoDate } from "@/lib/time/dates";

/** The request dialog's live line and its one chip (spec §1.4), from `ed-pto-quote`. */

export interface QuoteFacts {
  typeEnum: string;
  workDays: number;
  days: number;
  routedToHr: boolean;
  approverId: string | null;
  approverName: string | null;
  approverEmail: string | null;
  hrCount: number;
  hrEmail: string | null;
  hasClosedMonth: boolean;
  overDayCapDate: IsoDate | null;
  isLimited: boolean;
  /** The tightest checkpoint after this request ("Q4 2026", "2026"). */
  limitPeriod: string | null;
  limitUsed: number | null;
  limitAllowed: number | null;
}

export interface QuoteChip {
  tone: "danger" | "neutral";
  text: string;
}

export const QUOTE_MESSAGES = {
  noDays: "No working days in this range.",
  closed: "Includes a closed month. Won't submit.",
  noApprover: "No approver found. Ask HR.",
  toHr: "Goes to HR",
} as const;

const MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});

export function limitLeft(f: QuoteFacts): number | null {
  if (!f.isLimited || f.limitUsed === null || f.limitAllowed === null) return null;
  return round2(f.limitAllowed - f.limitUsed);
}

function overLimitText(f: QuoteFacts, by: number): string {
  const period = f.limitPeriod ?? "";
  const amount = formatDayCount(by);
  if (f.typeEnum === "vacation")
    return `Over your ${period.split(" ")[0]} balance by ${amount}. Won't submit.`;
  const what = f.typeEnum === "sick" ? "sick days" : "holidays";
  return `Over your ${period} ${what} by ${amount}. Won't submit.`;
}

/** At most one chip, in the gateway's rule order (spec R5, R7, R6, R8-R10, R14, then routing). */
export function quoteChip(f: QuoteFacts): QuoteChip | null {
  if (f.workDays <= 0) return { tone: "danger", text: QUOTE_MESSAGES.noDays };
  if (f.hasClosedMonth) return { tone: "danger", text: QUOTE_MESSAGES.closed };
  if (f.overDayCapDate) {
    const day = MONTH_DAY.format(new Date(`${f.overDayCapDate}T00:00:00Z`));
    return { tone: "danger", text: `More than 8 hrs of time off on ${day}. Won't submit.` };
  }
  const left = limitLeft(f);
  if (left !== null && left < 0) return { tone: "danger", text: overLimitText(f, -left) };
  if (f.hrCount !== 1 || f.approverId === null)
    return { tone: "danger", text: QUOTE_MESSAGES.noApprover };
  if (f.routedToHr) return { tone: "neutral", text: QUOTE_MESSAGES.toHr };
  return null;
}

export function quoteBlocked(f: QuoteFacts): boolean {
  return quoteChip(f)?.tone === "danger";
}

/** "4.5 days · 6.5 left · Martyn Mason". */
export function quoteSummary(f: QuoteFacts): string {
  const left = limitLeft(f);
  return [
    formatDayCount(f.days),
    left !== null && left >= 0 ? `${left} left` : null,
    f.approverName,
  ]
    .filter((p): p is string => Boolean(p))
    .join(" · ");
}

export type QuoteLine = "none" | "loading" | "error" | "shown";

/**
 * What the request dialog's live line shows. A failed quote is an error with a retry: never an
 * endless skeleton, and never an earlier answer for other dates (it would let a stale quote submit).
 */
export function quoteLine(q: {
  asked: boolean;
  hasData: boolean;
  isError: boolean;
  isFetching: boolean;
}): QuoteLine {
  if (!q.asked) return "none";
  if (q.isError) return q.isFetching ? "loading" : "error";
  return q.hasData ? "shown" : "loading";
}
